import { useMemo, useCallback, useState, useEffect } from 'react'

import { useApp } from '../../../../app/AppContext.jsx'
import { useProduct } from '../../index.js'

import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { Button, ButtonGroup } from '../../../../shared/ui/Button/index.js'
import { Car } from '../../../../shared/ui/icons/Car.jsx'
import { Modal } from '../../../../shared/ui/Modal/index.js'
import { StatusBadge } from '../../../../shared/ui/StatusBadge/index.js'
import { RecordTable } from '../../../../shared/ui/RecordTable/index.js'

import styles from './SupplementPickerModal.module.scss'

// ── Row helpers ────────────────────────────────────────────────────────────────

export function getRowKey(p) {
  return p.uuid ?? p.id_cad ?? p.crm_id_unit
}

/**
 * SupplementPickerModal — modal for selecting ancillary products (supplements).
 *
 * Displays available supplements grouped by category (parking_indoor, parking_outdoor, storage_units, etc.)
 * with category toggle buttons to filter the RecordTable. Shows add/remove toggles for each item.
 *
 * @param {object}     props
 * @param {boolean}    props.isOpen
 * @param {() => void} props.onClose
 */
export function SupplementPickerModal({ 
  isMobile, 
  isOpen, 
  onClose 
}) {
  const { t } = useI18n()
  const { panelSettings } = useApp()
  const { featureFlows } = useProduct()
  const { availableItems, selectedItems, addItem, removeItem, selectableCategories } = featureFlows.supplements

  const [sortColumnId, setSortColumnId] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')
  const [selectedCategory, setSelectedCategory] = useState(null)

  // Initialize selectedCategory to first available category on mount
  useEffect(() => {
    if (selectableCategories && selectableCategories.length > 0) {
      setSelectedCategory((prev) => prev || selectableCategories[0])
    }
  }, [selectableCategories])

  // CMS description or stub
  const supplementPurchasesOptions =
    panelSettings?.additional_unit_information?.supplement_purchases_options || {}

  const supplementPurchasesText = useMemo(() => {
    const cmsHtml = supplementPurchasesOptions?.description?.text_html
    if (cmsHtml && cmsHtml.trim().length > 0) return cmsHtml

    const localeKey = 'pl'
    const defaults = {
      pl: `<p>Zakup <strong>opcjonalnych produktów przynależnych</strong> możliwy jest w ramach oferty uzupełniającej, obejmującej między innymi:
        <ul><li>Miejsca postojowe</li><li>Komórki lokatorskie</li></ul>
      </p>`,
      en: `<p>Purchase of <strong>optional ancillary products</strong> is possible as part of a supplementary offer, including:
        <ul><li>Parking spaces</li><li>Storage units</li></ul>
      </p>`,
    }
    return defaults[localeKey] || defaults.pl
  }, [supplementPurchasesOptions])

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—'
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Filter items by selected category
  const itemsInSelectedCategory = useMemo(() => {
    if (!selectedCategory) return availableItems
    return availableItems.filter((item) => item.category === selectedCategory)
  }, [availableItems, selectedCategory])

  // Sort items
  const sortedItems = useMemo(() => {
    const sorted = [...itemsInSelectedCategory]

    if (sortColumnId === 'name') {
      sorted.sort((a, b) => {
        const aName = a.name ?? ''
        const bName = b.name ?? ''
        return sortDirection === 'asc'
          ? aName.localeCompare(bName)
          : bName.localeCompare(aName)
      })
    } else if (sortColumnId === 'area') {
      sorted.sort((a, b) => {
        const aArea = a.area_original ?? 0
        const bArea = b.area_original ?? 0
        return sortDirection === 'asc' ? aArea - bArea : bArea - aArea
      })
    } else if (sortColumnId === 'price') {
      sorted.sort((a, b) => {
        const aPrice = a.price_brutto ?? 0
        const bPrice = b.price_brutto ?? 0
        return sortDirection === 'asc' ? aPrice - bPrice : bPrice - aPrice
      })
    } else if (sortColumnId === 'status') {
      const statusOrder = { 'available': 1, 'reserved': 2, 'sold': 3 }
      sorted.sort((a, b) => {
        const aStatus = statusOrder[a.status] || 99
        const bStatus = statusOrder[b.status] || 99
        return sortDirection === 'asc' ? aStatus - bStatus : bStatus - aStatus
      })
    } else if (sortColumnId === 'action') {
      sorted.sort((a, b) => {
        const aSelected = selectedItems.some((s) => s.id === a.id) ? 1 : 0
        const bSelected = selectedItems.some((s) => s.id === b.id) ? 1 : 0
        return sortDirection === 'asc' ? aSelected - bSelected : bSelected - aSelected
      })
    }

    return sorted
  }, [itemsInSelectedCategory, sortColumnId, sortDirection])

  const handleSort = useCallback((colId, dir) => {
    setSortColumnId(colId)
    setSortDirection(dir)
  }, [])

  const isSelected = useCallback(
    (id) => selectedItems.some((s) => s.id === id),
    [selectedItems],
  )

  const handleToggle = useCallback(
    (item) => {
      if (isSelected(item.id)) {
        removeItem(item.id)
      } else {
        addItem(item.id)
      }
    },
    [isSelected, addItem, removeItem],
  )

  // Translated category labels
  const getCategoryLabel = (categoryKey) => {
    const labels = {
      parking_indoor: t('product.category_parking_indoor'),
      parking_outdoor: t('product.category_parking_outdoor'),
      storage_units: t('product.category_storage_units'),
    }
    return labels[categoryKey] || categoryKey
  }

  // RecordTable columns
  const columns = [
    {
      id: 'name',
      label: t('product.supplement_name_label'),
      width: '1fr',
      sortable: true,
      render: (item) => <span className={styles.nameCell}>{item.name}</span>,
    },
    {
      id: 'area',
      label: t('common.area_m2'),
      width: '8em',
      sortable: true,
      align: 'right',
      render: (item) => (
        <span className={styles.areaCell}>
          {item.area_original != null ? `${item.area_original.toFixed(2)} m²` : '—'}
        </span>
      ),
    },
    {
      id: 'status',
      label: t('common.status'),
      width: '10em',
      sortable: true,
      align: 'center',
      render: (item) => (
        <span className={styles.statusCell}>
          <StatusBadge 
            // size='small'
            status={item.status ?? '—'} 
          />
        </span>
      ),
    },
    {
      id: 'price',
      label: t('common.price'),
      width: '10em',
      sortable: true,
      align: 'right',
      render: (item) => (
        <span className={styles.priceCell}>
          {formatCurrency(item.price_brutto)}
        </span>
      ),
    },
    {
      id: 'action',
      label: '',
      width: '8em',
      align: 'center',
      sortable: true,
      render: (item) => {
        const selected = isSelected(item.id)
        return (
          <Button
            type="button"
            kind="text"
            size="small"
            selected={selected}
            onClick={() => handleToggle(item)}
            aria-pressed={selected}
          >
            {selected
              ? t('product.picker_added')
              : t('product.picker_add')}
          </Button>
        )
      },
    },
  ]

  const headingIconSize = isMobile ? '2em' : '1.2em'
  const togglesSize = isMobile ? 'small' : 'medium'
  const togglesButtonWidth = '12em'

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('product.supplements_picker_title')}
    >
      <Modal.Header onClose={onClose} closeLabel={t('common.close')}>
        <span className={styles.heading}>
          <Car width={headingIconSize} height={headingIconSize} />
          {t('product.picker_title')}
        </span>
      </Modal.Header>
      <Modal.Content>
        <div className={styles.supplementsPane}>
          <div className={styles.descriptionContainer}>
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: supplementPurchasesText }}
            />
          </div>

          {selectableCategories.length > 1 && (
            <div className={styles.toggleButtonsContainer}>
              <ButtonGroup 
                sliding
                size={togglesSize}
                style={{ 
                  width: `calc(${selectableCategories.length} * ${togglesButtonWidth} + 2px)`, 
                  flexShrink: 0 
                }}
              >
                {selectableCategories.map((categoryKey) => (
                  <Button
                    key={categoryKey}
                    type="button"
                    kind='text'
                    size={togglesSize}
                    selected={categoryKey === selectedCategory}
                    onClick={() => setSelectedCategory(categoryKey)}
                    style={{ width: togglesButtonWidth }}
                  >
                    {getCategoryLabel(categoryKey)}
                  </Button>
                  ))}
              </ButtonGroup>
            </div>
          )}

          <div className={styles.tableContainer}>
            {sortedItems.length > 0 ? (
              <RecordTable
                rows={sortedItems}
                getRowKey={getRowKey}
                columns={columns}
                sortColumnId={sortColumnId}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            ) : (
              <div className={styles.noItems}>
                {t('product.no_supplements_available')}
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
    </Modal>
  )
}
